import { useState, type ReactNode } from 'react';
import { Popover } from '@mantine/core';
import { IoChevronDown, IoSearchOutline } from 'react-icons/io5';
import Input from '../input';
import './QuietSelect.css';

export interface QuietSelectOption<T extends string | number> {
  value: T;
  label: ReactNode;
  markerTitle?: string;
}

export interface QuietSelectProps<T extends string | number> {
  value: T | null;
  onChange: (value: T) => void;
  options: QuietSelectOption<T>[];
  label: ReactNode;
  ariaLabel: string;
  triggerMarkerTitle?: string;
  search?: {
    value: string;
    onChange: (value: string) => void;
    placeholder: string;
  };
  emptyMessage?: string;
  className?: string;
}

export default function QuietSelect<T extends string | number>({
  value,
  onChange,
  options,
  label,
  ariaLabel,
  triggerMarkerTitle,
  search,
  emptyMessage = 'Ничего не найдено',
  className,
}: QuietSelectProps<T>) {
  const [opened, setOpened] = useState(false);

  const setOpenedSafe = (next: boolean) => {
    setOpened(next);
    if (!next && search && search.value !== '') {
      search.onChange('');
    }
  };

  return (
    <div className={['quiet-select', className].filter(Boolean).join(' ')}>
      <Popover
        opened={opened}
        onChange={setOpenedSafe}
        position="bottom-end"
        withArrow
        shadow="md"
        classNames={{ dropdown: 'quiet-select__dropdown' }}
      >
        <Popover.Target>
          <button
            type="button"
            className={`quiet-select__trigger${opened ? ' quiet-select__trigger--open' : ''}`}
            onClick={() => setOpenedSafe(!opened)}
            aria-label={ariaLabel}
            aria-expanded={opened}
          >
            <span className="quiet-select__label">{label}</span>
            <span className="quiet-select__chevron">
              <IoChevronDown size={14} aria-hidden />
              {triggerMarkerTitle ? (
                <span className="quiet-select__trigger-marker" title={triggerMarkerTitle} aria-hidden />
              ) : null}
            </span>
          </button>
        </Popover.Target>
        <Popover.Dropdown>
          {search ? (
            <div className="quiet-select__search">
              <Input
                className="quiet-select__search-input"
                type="text"
                placeholder={search.placeholder}
                value={search.value}
                onChange={(event) => search.onChange(event.target.value)}
                leftAddon={<IoSearchOutline size={16} aria-hidden />}
                autoFocus
              />
            </div>
          ) : null}
          <div className="quiet-select__options" role="menu" aria-label={ariaLabel}>
            {options.length > 0 ? (
              options.map((option) => {
                const isActive = option.value === value;
                return (
                  <button
                    key={String(option.value)}
                    type="button"
                    role="menuitemradio"
                    aria-checked={isActive}
                    className={`quiet-select__option${isActive ? ' quiet-select__option--active' : ''}`}
                    onClick={() => {
                      onChange(option.value);
                      setOpenedSafe(false);
                    }}
                  >
                    <span className="quiet-select__option-label">{option.label}</span>
                    {option.markerTitle ? (
                      <span
                        className="quiet-select__option-marker"
                        title={option.markerTitle}
                        aria-label={option.markerTitle}
                      />
                    ) : null}
                  </button>
                );
              })
            ) : (
              <div className="quiet-select__empty">{emptyMessage}</div>
            )}
          </div>
        </Popover.Dropdown>
      </Popover>
    </div>
  );
}
